import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'default' | 'large' | 'full';
}

export function BaseModal({ isOpen, onClose, title, children, size = 'default' }: BaseModalProps) {
  const [isExiting, setIsExiting] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);

    if (isOpen) {
      setShouldRender(true);
      setIsExiting(false);
      document.body.classList.add('modal-open');
    } else if (!isExiting) {
      handleClose();
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      setShouldRender(false);
      setIsExiting(false);
      document.body.classList.remove('modal-open');
      onClose();
    }, 200);
  };

  if (!shouldRender) return null;

  const backdropClass = isExiting ? 'modal-backdrop-exit' : 'modal-backdrop-enter';
  const contentClass = isExiting ? 'modal-content-exit' : 'modal-content-enter';

  const sizeClasses = {
    default: 'sm:max-w-2xl',
    large: 'sm:max-w-4xl',
    full: 'sm:max-w-7xl'
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className={`fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity ${backdropClass}`} 
          aria-hidden="true" 
          onClick={handleClose}
        />

        {/* This element is to trick the browser into centering the modal contents. */}
        <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>

        {/* Modal panel */}
        <div 
          className={`inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full ${sizeClasses[size]} ${contentClass}`}
        >
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 transition-colors duration-200"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{title}</h2>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}